import React, { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Input, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton,
} from '@redpanda/ui';
import { useUnsavedChangesWarning } from '@redpanda/utils';
import { bankAccountService } from '../services/api';
import { BankTransaction, TRANSACTION_TYPES, transactionStoreFor } from '../stores/bankAccountStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { dateInputValue, todayInput, toUtcMidnight } from '../utils/dates';

interface BankTransactionDialogProps {
  accountId: string;
  /** 有值＝編輯；沒有＝新增 */
  transaction?: BankTransaction;
  onClose: () => void;
}

interface FormData {
  transactionDate: string;
  transactionType: string;
  amount: string;
  description: string;
  reference: string;
}

const emptyForm = (): FormData => ({ transactionDate: todayInput(), transactionType: 'DEPOSIT', amount: '', description: '', reference: '' });

/**
 * 新增／編輯交易記錄：等伺服器回應才關（交易後餘額要由後端依日期重算）
 * 金額填正數，正負由交易類型決定（提款、轉出、手續費為負）；原本編輯只改畫面、沒有存到後端
 */
const BankTransactionDialog: React.FC<BankTransactionDialogProps> = ({ accountId, transaction, onClose }) => {
  const { t } = useTranslation();
  const isEditMode = !!transaction;
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, formState: { errors, isDirty }, reset, setValue, watch } = useForm<FormData>({ defaultValues: emptyForm() });

  useLayoutEffect(() => {
    reset(transaction ? {
      transactionDate: dateInputValue(transaction.transactionDate),
      transactionType: transaction.transactionType,
      amount: String(Math.abs(Number(transaction.amount))),
      description: transaction.description,
      reference: transaction.reference || '',
    } : emptyForm());
  }, [transaction, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty && !saving, onClose);

  const onSubmit = async (data: FormData) => {
    const payload = {
      transactionDate: toUtcMidnight(data.transactionDate),
      transactionType: data.transactionType,
      amount: Number(data.amount),
      description: data.description.trim(),
      reference: data.reference.trim() || null,
    };
    setSaving(true);
    try {
      const store = transactionStoreFor(accountId).getState();
      if (transaction) {
        const { data: saved } = await bankAccountService.updateBankTransaction(accountId, transaction.id, payload);
        store.updateItem(transaction.id, saved);
        toast.success(t('common.messages.updateSuccess'));
      } else {
        const { data: saved } = await bankAccountService.createBankTransaction(accountId, payload);
        store.addItem(saved);
        toast.success(t('common.messages.createSuccess'));
      }
      // 其他筆的交易後餘額、帳戶餘額都可能變了：列表重抓
      await transactionStoreFor(accountId).getState().refreshData();
      notifyDataChanged('bankTransactions');
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, transaction ? 'common.messages.updateFailed' : 'common.messages.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const amountField = register('amount', {
    validate: value => Number(value) > 0 || t('bankTransactions.dialog.amountMin'),
  });

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={saving} className="max-w-lg">
        <DialogHeader
          title={isEditMode ? t('bankTransactions.dialog.editTitle') : t('bankTransactions.dialog.addTitle')}
          onClose={handleClose}
          closeDisabled={saving}
          closeLabel={t('common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormFieldWithError label={t('bankTransactions.dialog.date')} required error={errors.transactionDate?.message} htmlFor="transactionDate">
                <Input
                  id="transactionDate"
                  type="date"
                  {...register('transactionDate', { required: t('common.forms.validation.required') })}
                  variant={errors.transactionDate ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <FormFieldWithError label={t('bankTransactions.dialog.type')} required htmlFor="transactionType">
                <CustomSelect
                  id="transactionType"
                  value={watch('transactionType')}
                  onChange={value => setValue('transactionType', value, { shouldDirty: true })}
                  options={TRANSACTION_TYPES.map(type => ({ value: type, label: t(`bankTransactions.types.${type}`) }))}
                />
              </FormFieldWithError>
            </div>
            <FormFieldWithError label={t('bankTransactions.dialog.amount')} required error={errors.amount?.message} helpText={t('bankTransactions.dialog.amountHint')} htmlFor="amount">
              <Input
                id="amount"
                inputMode="decimal"
                className="text-right tabular-nums"
                {...amountField}
                onChange={event => {
                  event.target.value = event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
                  return amountField.onChange(event);
                }}
                variant={errors.amount ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <FormFieldWithError label={t('bankTransactions.dialog.description')} required error={errors.description?.message} htmlFor="description">
              <Input
                id="description"
                {...register('description', { validate: value => !!value.trim() || t('common.forms.validation.required') })}
                variant={errors.description ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <FormFieldWithError label={t('bankTransactions.dialog.reference')} htmlFor="reference">
              <Input id="reference" {...register('reference')} />
            </FormFieldWithError>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} disabled={saving} className={dialogButtonClass.cancel}>
              {t('common.actions.cancel')}
            </button>
            <LoadingButton type="submit" loading={saving} className={dialogButtonClass.primary}>
              {isEditMode ? t('common.actions.save') : t('common.actions.create')}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('modal:unsavedChanges.title')}
        message={t('modal:unsavedChanges.message')}
        confirmText={t('modal:unsavedChanges.confirmButton')}
        cancelText={t('modal:unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default BankTransactionDialog;
